import { useEffect, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

interface Options {
  pageSize?: number;
  initialKey?: string;
  initialDirection?: SortDirection;
}

/**
 * Controla el ordenamiento y la paginación de una lista de registros.
 * `getSortValue` obtiene el valor comparable de cada fila según la columna.
 */
export function useTableControls<T>(
  rows: T[],
  getSortValue: (row: T, key: string) => string | number,
  options: Options = {},
) {
  const { pageSize: initialPageSize = 10, initialKey = '', initialDirection = 'asc' } = options;

  const [sortKey, setSortKey] = useState(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const valueA = getSortValue(a, sortKey);
      const valueB = getSortValue(b, sortKey);
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return direction === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const comparison = String(valueA).localeCompare(String(valueB), 'es', { sensitivity: 'base' });
      return direction === 'asc' ? comparison : -comparison;
    });
    return copy;
  }, [rows, sortKey, direction, getSortValue]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pagedRows = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setDirection('asc');
    }
    setPage(1);
  };

  return {
    rows: pagedRows,
    total: sorted.length,
    sortKey,
    direction,
    toggleSort,
    page,
    setPage,
    pageCount,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
  };
}